/**
 * CONTROL messages of the transfer protocol (the data goes in binary
 * via `framing.ts`). Versioned JSON (`v:1`) and VALIDATED on receive: a peer
 * never trusts the wire. `decodeControl` returns `null` for anything
 * it does not understand (truncated, future version, garbage) → the caller discards it.
 *
 * Flow (sender E, receiver R):
 *   E → R  offer     manifest: list of files {name,size,mime}
 *   R → E  accept    accepts; `resume[i]` = bytes ALREADY received of file i
 *   R → E  reject    rejects the whole transfer
 *   E → R  fileStart starts file i at `offset` (0 or resumption point)
 *   E → R  fileEnd   file i complete; `sha256` for final verification
 *   R → E  fileAck   R confirms file i intact (or requests resend: ok=false)
 *   E → R  text      text / clipboard message (no file)
 *   E → R  abort     the sender gives up (e.g. unrecoverable integrity)
 *   *      bye       orderly close
 *
 * Reliability: if `fileAck.ok === false`, the sender RESENDS the whole file
 * (new `fileStart` at offset 0 + chunks + `fileEnd`) up to `maxFileRetries`
 * times; if exhausted, it sends `abort` and closes. The receiver does NOT treat a
 * SHA failure as fatal: it waits for the resend and only fails on `abort`/close.
 */

import { isRecord } from "../p2p/guards";

export const TRANSFER_PROTOCOL_VERSION = 1;

export interface FileMeta {
  name: string;
  size: number;
  mime: string;
}

export type ControlMessage =
  | { t: "offer"; v: number; files: FileMeta[] }
  | { t: "accept"; v: number; resume: number[] }
  | { t: "reject"; v: number; reason: string }
  | { t: "fileStart"; v: number; i: number; offset: number }
  | { t: "fileEnd"; v: number; i: number; sha256: string }
  | { t: "fileAck"; v: number; i: number; ok: boolean }
  | { t: "text"; v: number; body: string }
  | { t: "abort"; v: number; reason: string }
  | { t: "bye"; v: number };

export function encodeControl(message: ControlMessage): string {
  return JSON.stringify(message);
}

const MAX_CONTROL_BYTES = 256 * 1024; // manifiestos de muchos ficheros caben holgados

/**
 * File cap per manifest. Protects the receiver from a giant `offer`
 * (creating thousands of sinks/UI entries at once). A normal folder fits
 * easily; for larger trees the sender must split the send.
 */
export const MAX_OFFER_FILES = 4096;

function isFileMeta(value: unknown): value is FileMeta {
  return (
    isRecord(value) &&
    typeof value.name === "string" &&
    typeof value.size === "number" &&
    Number.isInteger(value.size) &&
    value.size >= 0 &&
    typeof value.mime === "string"
  );
}

function isNonNegIntArray(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.every((n) => typeof n === "number" && Number.isInteger(n) && n >= 0)
  );
}

/** Parses and validates a control message; `null` = discard. */
export function decodeControl(raw: unknown): ControlMessage | null {
  if (typeof raw !== "string" || raw.length > MAX_CONTROL_BYTES) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.v !== TRANSFER_PROTOCOL_VERSION) {
    return null;
  }
  const v = TRANSFER_PROTOCOL_VERSION;
  switch (parsed.t) {
    case "offer": {
      if (
        Array.isArray(parsed.files) &&
        parsed.files.length <= MAX_OFFER_FILES &&
        parsed.files.every(isFileMeta)
      ) {
        return { t: "offer", v, files: parsed.files };
      }
      return null;
    }
    case "accept": {
      if (isNonNegIntArray(parsed.resume)) {
        return { t: "accept", v, resume: parsed.resume };
      }
      return null;
    }
    case "reject": {
      if (typeof parsed.reason === "string") {
        return { t: "reject", v, reason: parsed.reason };
      }
      return null;
    }
    case "fileStart": {
      if (isIndex(parsed.i) && isOffset(parsed.offset)) {
        return { t: "fileStart", v, i: parsed.i, offset: parsed.offset };
      }
      return null;
    }
    case "fileEnd": {
      if (isIndex(parsed.i) && typeof parsed.sha256 === "string") {
        return { t: "fileEnd", v, i: parsed.i, sha256: parsed.sha256 };
      }
      return null;
    }
    case "fileAck": {
      if (isIndex(parsed.i) && typeof parsed.ok === "boolean") {
        return { t: "fileAck", v, i: parsed.i, ok: parsed.ok };
      }
      return null;
    }
    case "text": {
      if (typeof parsed.body === "string") {
        return { t: "text", v, body: parsed.body };
      }
      return null;
    }
    case "abort": {
      if (typeof parsed.reason === "string") {
        return { t: "abort", v, reason: parsed.reason };
      }
      return null;
    }
    case "bye": {
      return { t: "bye", v };
    }
    default: {
      return null;
    }
  }
}

function isIndex(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isOffset(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}
